'use client';

import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { THEME_COOKIE, type ThemeMode } from '@/lib/theme/tokens';

interface ThemeSelectorRadialProps {
  current: ThemeMode;
}

interface Option {
  value: ThemeMode;
  labelKey: 'theme.light' | 'theme.system' | 'theme.dark';
  icon: LucideIcon;
}

const OPTIONS: readonly Option[] = [
  { value: 'light', labelKey: 'theme.light', icon: Sun },
  { value: 'system', labelKey: 'theme.system', icon: Monitor },
  { value: 'dark', labelKey: 'theme.dark', icon: Moon },
];

/**
 * Segmented "radial" selector — three circular icon buttons in a pill.
 * The active option fills with the accent color. Lives in the top navbar.
 */
export function ThemeSelectorRadial({ current }: ThemeSelectorRadialProps) {
  const t = useTranslations('common');
  const [mode, setMode] = React.useState<ThemeMode>(current);
  React.useEffect(() => setMode(current), [current]);

  function applyResolved(next: ThemeMode) {
    if (typeof document === 'undefined') return;
    let resolved: 'light' | 'dark';
    if (next === 'system') {
      resolved = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    } else {
      resolved = next;
    }
    document.documentElement.setAttribute('data-theme', resolved);
  }

  function persist(next: ThemeMode) {
    const maxAge = 60 * 60 * 24 * 365;
    document.cookie = `${THEME_COOKIE}=${encodeURIComponent(next)}; path=/; max-age=${maxAge}; samesite=lax`;
  }

  function apply(next: ThemeMode) {
    if (next === mode) return;
    setMode(next);
    applyResolved(next);
    persist(next);
  }

  function smartToggle() {
    const painted =
      document.documentElement.getAttribute('data-theme') === 'dark'
        ? 'dark'
        : 'light';
    apply(painted === 'dark' ? 'light' : 'dark');
  }

  function handleContainerClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) smartToggle();
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div
        role="radiogroup"
        aria-label={t('theme.groupLabel')}
        onClick={handleContainerClick}
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full border border-border bg-surface-muted p-0.5',
          'shadow-inner',
        )}
      >
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = opt.value === mode;
          const label = t(opt.labelKey);
          return (
            <Tooltip key={opt.value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={label}
                  onClick={() => (active ? smartToggle() : apply(opt.value))}
                  className={cn(
                    'relative inline-flex h-7 w-7 items-center justify-center rounded-full transition-all',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:z-10',
                    active
                      ? 'bg-accent text-accent-foreground shadow'
                      : 'text-foreground-muted hover:text-foreground hover:bg-accent-soft',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{label}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
